/**
 * Phase 5A: top-of-detail-page date-range strip. Preset chips +
 * custom-range pickers + IST-resolved start/end label.
 *
 * State is sourced from + written back to `?start=&end=&preset=` URL params
 * so a refresh/share round-trips the chosen window. The strip never owns
 * state internally — the parent passes (preset, start, end) and we report
 * changes via `onChange`.
 *
 * Preset semantics — all dates resolve in IST:
 *   today  → start=end=today.
 *   7d     → end=today, start=today-6 (inclusive 7 days).
 *   30d    → end=today, start=today-29.
 *   90d    → end=today, start=today-89.
 *   fy     → start=Apr-1 (Indian FY); end=today (or Mar-31 of FY end).
 *   ytd    → start=Jan-1; end=today.
 *   1y     → end=today, start=today-364.
 *   all    → start=basket's published_at floor (rendered as "All" chip;
 *            backend resolves the floor — we just send preset=all).
 *   custom → both pickers exposed; preset undefined when in use.
 */
import React, { useMemo } from 'react';
import { CalendarDays } from 'lucide-react';
import type { DateRangePreset } from '../../types/marketplaceAnalytics';
import { cn } from '../../utils/cn';
import { getTodayIST, subDaysIso } from '../../utils/dateUtils';

interface Props {
  preset: DateRangePreset;
  start: string;
  end: string;
  onChange: (next: { preset: DateRangePreset; start: string; end: string }) => void;
}

const presets: Array<{ key: DateRangePreset; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
  { key: '90d', label: '90d' },
  { key: 'fy', label: 'FY' },
  { key: 'ytd', label: 'YTD' },
  { key: '1y', label: '1Y' },
  { key: 'all', label: 'All' },
];

const resolvePreset = (preset: DateRangePreset): { start: string; end: string } => {
  // IST date arithmetic anchored via `getTodayIST()` (uses `Intl` +
  // `Asia/Kolkata`) and `subDaysIso()` (noon-UTC anchored) — both
  // exported from `utils/dateUtils.ts`. Pre-fix `istToday()` copies
  // used `now.getTime() + IST_OFFSET_MS - (now.getTimezoneOffset() * 60 * 1000)`
  // which double-shifts in IST browsers and returned TOMORROW's date
  // from 13:00 IST onwards — every preset chip click resolved to the
  // wrong window.
  const today = getTodayIST();  // 'YYYY-MM-DD'
  const [ty, tm] = today.split('-').map(Number);
  switch (preset) {
    case 'today':
      return { start: today, end: today };
    case '7d':
      return { start: subDaysIso(today, 6), end: today };
    case '30d':
      return { start: subDaysIso(today, 29), end: today };
    case '90d':
      return { start: subDaysIso(today, 89), end: today };
    case 'fy': {
      // Indian FY runs Apr 1 → Mar 31. Before April, the current FY
      // started last calendar year.
      const y = tm >= 4 ? ty : ty - 1;
      return { start: `${y}-04-01`, end: today };
    }
    case 'ytd':
      return { start: `${ty}-01-01`, end: today };
    case '1y':
      return { start: subDaysIso(today, 364), end: today };
    case 'all':
      // Backend resolves the floor (basket's published_at). Send a wide
      // hint here so the URL is honest about scope.
      return { start: '0000-01-01', end: today };
    default:
      return { start: subDaysIso(today, 29), end: today };
  }
};

const MarketplaceDateRangeStrip: React.FC<Props> = ({
  preset, start, end, onChange,
}) => {
  const rangeLabel = useMemo(() => {
    if (!start || !end) return '';
    const fmt = (s: string) => {
      const [y, m, d] = s.split('-');
      if (!y || !m || !d) return s;
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${d} ${months[Number(m) - 1] || m} ${y}`;
    };
    return `${fmt(start)} → ${fmt(end)}`;
  }, [start, end]);

  const handlePreset = (next: DateRangePreset) => {
    const { start: s, end: e } = resolvePreset(next);
    onChange({ preset: next, start: s, end: e });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
      <CalendarDays className="h-4 w-4 text-gray-500 dark:text-gray-400" />
      <div className="flex flex-wrap items-center gap-1">
        {presets.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => handlePreset(key)}
            className={cn(
              'text-xs font-medium px-2.5 py-1 rounded-md transition-colors',
              preset === key
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700',
            )}
            aria-pressed={preset === key}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="hidden sm:flex items-center gap-1 ml-2">
        <input
          type="date"
          value={start}
          onChange={(e) => onChange({ preset: 'custom', start: e.target.value, end })}
          className="h-7 px-2 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
          aria-label="Start date"
        />
        <span className="text-gray-400">→</span>
        <input
          type="date"
          value={end}
          onChange={(e) => onChange({ preset: 'custom', start, end: e.target.value })}
          className="h-7 px-2 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
          aria-label="End date"
        />
      </div>
      <div className="ml-auto text-xs text-gray-500 dark:text-gray-400 font-mono">
        {rangeLabel}
      </div>
    </div>
  );
};

export default MarketplaceDateRangeStrip;
